// Libellés du journal d'audit (actions, champs, auteurs, valeurs).
import moment from "moment";

export const CATEGORIES = {
  metier: { label: "Métier", color: "blue" },
  acces: { label: "Accès", color: "purple" },
  technique: { label: "Technique", color: "default" },
};

const TARGET_TYPES = {
  message: "Envoi",
  recipient: "Destinataire",
  user: "Utilisateur",
  company: "Compte",
  contact: "Contact",
  list: "Liste",
  return_address: "Adresse de retour",
  tracking_pool: "Plage de suivi",
  service_account: "Compte de service",
  api_key: "Clé d'API",
  certificate: "Certificat client",
};

const OPERATIONS = { create: "création", update: "modification", delete: "suppression" };

export const EVENT_ACTIONS = {
  "delegation.start": "Connexion déléguée ouverte",
  "delegation.end": "Connexion déléguée terminée",
  "session.sign_in": "Connexion",
  "user.password_set": "Mot de passe défini par un admin",
  "user.password_set_failed": "Échec du changement de mot de passe",
  "export.user_stats": "Export des envois (CSV)",
  "export.printing_stats": "Export comptable (Excel)",
  "liasse.generate": "Génération de liasses",
  "ar_pool.import": "Import d'une plage de numéros AR",
  "maintenance.backfill_sent_at": "Rattrapage des dates de dépôt",
  "lre.accepted": "LRE acceptée par le destinataire",
  "lre.refused": "LRE refusée par le destinataire",
  "service_account.create": "Compte de service créé",
  "service_account.update": "Compte de service modifié",
  "api_key.create": "Clé d'API créée",
  "api_key.revoke": "Clé d'API révoquée",
  "certificate.add": "Certificat client enregistré",
  "certificate.revoke": "Certificat client révoqué",
};

// Actions proposées dans le filtre
export const ACTION_OPTIONS = [
  ...Object.keys(TARGET_TYPES)
    .filter(
      (type) => !["tracking_pool", "service_account", "api_key", "certificate"].includes(type)
    )
    .flatMap((type) => Object.keys(OPERATIONS).map((operation) => `${type}.${operation}`)),
  ...Object.keys(EVENT_ACTIONS),
];

export function actionLabel(action) {
  if (EVENT_ACTIONS[action]) return EVENT_ACTIONS[action];
  const [type, operation] = String(action || "").split(".");
  if (TARGET_TYPES[type] && OPERATIONS[operation]) {
    return `${TARGET_TYPES[type]} : ${OPERATIONS[operation]}`;
  }
  return action || "—";
}

export const FIELDS = {
  status: "Statut",
  pages: "Pages imprimées",
  addresses_count: "Nombre d'adresses",
  trackingNumber: "N° de suivi",
  trackingType: "Type de suivi",
  tracking_number_ref: "Numéro de la plage",
  sent_at: "Date de dépôt (compta)",
  send_date: "Date d'envoi",
  send_type: "Type d'envoi",
  cancelled_at: "Annulé le",
  cancelled_by: "Annulé par",
  date: "Date d'envoi prévue",
  date_type: "Envoi immédiat / différé",
  created_at: "Créé le",
  color_option: "Couleur",
  face_option: "Recto / verso",
  recipients_type: "Type de destinataires",
  envelope: "Délai de distribution",
  receipt_aknowledge: "Accusé de réception",
  level: "Niveau de recommandation",
  ar_generated_at: "Liasses générées le",
  ar_batch_pdf_url: "Lot de liasses (fichier)",
  user: "Expéditeur",
  created_via: "Créé via",
  api_key_id: "Clé d'API utilisée",
  client_certificate_sha256: "Certificat client (empreinte)",
  api_access: "Accès API",
  attachments: "Documents",
  subject: "Objet",
  content: "Message",
  ref1: "Référence 1",
  ref2: "Référence 2",
  comment: "Instructions d'impression",
  trackingFile: "Preuve de dépôt (fichier)",
  trackingPath: "Preuve de dépôt (chemin)",
  contacts: "Destinataires sélectionnés",
  type: "Type",
  country: "Pays",
  country_code: "Code pays",
  return_address_name: "Adresse de retour : nom",
  return_address_line_1: "Adresse de retour : ligne 1",
  return_address_line_2: "Adresse de retour : ligne 2",
  return_address_postal_code: "Adresse de retour : code postal",
  return_address_city: "Adresse de retour : ville",
  is_sent: "Envoyé",
  company: "Compte",
  message: "Envoi",
  proofs: "Preuves",
  token: "Lien sécurisé",
  first_name: "Prénom",
  last_name: "Nom",
  email: "E-mail",
  phone: "Téléphone",
  address_line_1: "Adresse",
  address_line_2: "Complément d'adresse",
  postal_code: "Code postal",
  city: "Ville",
  company_name: "Société",
  reference: "Référence",
  isAdmin: "Super administrateur",
  isOperator: "Opérateur",
  is_company_admin: "Administrateur de compte",
  is_account_admin: "Administrateur d'équipe",
  admin: "Admin (ancien champ)",
  disabled: "Désactivé",
  has2FAActivated: "Solidoc Authenticator",
  phone2Fa: "Téléphone 2FA",
  isActive: "Compte activé",
  force2FA: "2FA obligatoire",
  last_invite: "Invitation envoyée",
  invited_by: "Invité par",
  name: "Nom",
  sendOptions: "Options d'envoi",
  allowSubAccounts: "Sous-comptes autorisés",
  maxSubAccounts: "Sous-comptes (maximum)",
  parentCompany: "Compte parent",
  accounting_name: "Nom comptable",
  createdBy: "Créé par (origine)",
  createdAt: "Créé le",
  createdByUser: "Créé par",
  label: "Libellé",
  is_default: "Adresse par défaut",
};

export function fieldLabel(field) {
  return FIELDS[field] || field;
}

const STATUSES = {
  prepare: "En préparation",
  scheduled: "Programmé",
  sent: "Envoyé",
  accepted: "Accepté / distribué",
  refused: "Refusé",
  neglected: "Négligé",
  cancelled: "Annulé",
  error_multiple: "Erreurs multiples",
  error_address: "Erreur d'adressage",
  error_file: "Erreur fichier",
  error_print: "Erreur d'impression",
  error_print_param: "Erreur paramètres d'impression",
};

const ROLES = {
  admin: "admin Solidoc",
  operator: "opérateur",
  company_admin: "admin de compte",
  account_admin: "admin d'équipe",
  user: "utilisateur",
};

export function actorLabel(actor) {
  if (!actor) return "Non attribué";
  switch (actor.type) {
    case "system":
      return "Système";
    case "recipient":
      return "Destinataire (lien LRE)";
    case "service_account":
      return `Compte de service ${actor.name || actor.uid || ""} (API)`;
    case "user": {
      const who = actor.email || actor.uid;
      const role = ROLES[actor.role] ? ` (${ROLES[actor.role]})` : "";
      if (actor.delegated_by) {
        return `${
          actor.delegated_by_email || actor.delegated_by
        } en connexion déléguée pour ${who}`;
      }
      return `${who}${role}`;
    }
    default:
      return "Non attribué";
  }
}

export function viaLabel(source) {
  const via = source && source.via;
  if (!via) return "—";
  if (via === "web") return "Application";
  if (via === "api") return "API";
  const [kind, name] = via.split(":");
  if (kind === "function") return `Fonction ${name}`;
  if (kind === "trigger") return `Automatique (${name})`;
  if (kind === "scheduler") return `Tâche planifiée (${name})`;
  return via;
}

export const ATTRIBUTIONS = {
  stamp: { label: "Tampon vérifié", color: "green" },
  server: { label: "Serveur", color: "blue" },
  none: { label: "Non attribuée", color: "orange" },
};

export function targetLabel(target) {
  if (!target) return "—";
  const type = TARGET_TYPES[target.type] || target.type || "—";
  const id = target.recipient_id || target.message_id || target.user_id || target.id;
  return id ? `${type} ${String(id).toUpperCase()}` : type;
}

function isTimestamp(value) {
  return value && typeof value.toDate === "function";
}

export function formatDate(value) {
  if (!value) return "—";
  const date = isTimestamp(value) ? value.toDate() : value;
  return moment(date).format("DD/MM/YYYY HH:mm:ss");
}

// Valeur journalisée (avant / après) → texte lisible
export function formatValue(field, value) {
  if (value === null || value === undefined || value === "") return "—";
  if (isTimestamp(value)) return formatDate(value);
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (field === "status" && STATUSES[value]) return STATUSES[value];
  if (field === "attachments" && typeof value === "object") {
    return `${value.count} fichier(s), ${value.completed} traité(s), ${value.pages} page(s)`;
  }
  if (field === "proofs" && Array.isArray(value)) {
    return value.length
      ? value.map((p) => `${STATUSES[p.type] || p.type} (${formatDate(p.date)})`).join(", ")
      : "aucune";
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
